/**
 * Export the focal space as portable vector SVG — rendered headless from
 * the placed BoardSpace via the scene kernel (`renderBoardSvg`), NOT
 * captured from the live DOM. Real <rect>/<text>/<path> output: opens in
 * Illustrator/Inkscape/GitHub, no <foreignObject>, no browser dependency.
 */
import {
  applyFocus,
  applyLifecycle,
  composeBoardNode,
  renderBoardSvg,
  type BoardItem,
  type BoardSpace,
} from "@archlang/scene/board";
import type { LifecyclePaintMap } from "@archlang/viewer-core";
import { BG, triggerDownload, type ExportFocalOptions } from "./exportShared";
import { resolveSceneIcon } from "./sceneIcon";

/**
 * Compose the focal space to a portable SVG string (pure, no DOM).
 *
 * `lifecycle` is the paint the board is CURRENTLY showing. Without it the file
 * disagreed with the screen it came from: a board scrubbed past a sunset drew
 * the retired element greyed, struck and badged, and downloaded it identical to
 * a live one. `applyLifecycle` folds it into the space — nodes, edges and ports
 * together — and the composer reads each item's paint from the item, so this
 * export cannot key paint differently from the headless one.
 *
 * `focus` is the same argument one layer out: the figure/ground split a view's
 * `focus` clause makes is a STANDING property of the model, so a file exported
 * from a focused board has to carry it or the export contradicts the screen in
 * exactly the way lifecycle used to. `applyFocus` folds the roles on and the
 * veil is drawn at the item layer, in this canvas's own colour (`BG`) — so a
 * receded node keeps every pixel of its paint and simply sits behind glass.
 *
 * The `nodeFor` hook that remains is only for the icon resolver, which is a
 * browser concern the headless renderer has no equivalent for.
 */
export function focalSceneSvg(
  focal: BoardSpace,
  opts: Pick<ExportFocalOptions, "padding"> & {
    lifecycle?: LifecyclePaintMap;
    /** Board keys (`boardItemKey`) the active view marks as figure. */
    focus?: ReadonlySet<string>;
  } = {},
): string {
  const { padding = 96, lifecycle, focus } = opts;
  return renderBoardSvg(applyFocus(applyLifecycle(focal, lifecycle), focus), {
    nodeFor: (item: BoardItem) => composeBoardNode(item, { resolveIcon: resolveSceneIcon }),
    background: BG,
    padding,
  });
}

export async function exportFocalAsSvg(
  focal: BoardSpace,
  opts: ExportFocalOptions = {},
): Promise<void> {
  const { filename = "archlang-space.svg" } = opts;
  triggerDownload(
    new Blob([focalSceneSvg(focal, opts)], { type: "image/svg+xml;charset=utf-8" }),
    filename,
  );
}
